import React, { useCallback, useMemo, useContext, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Input, Loader } from 'ncoded-component-library';
import { MessageType, SessionType } from 'models/Message';
import { useNavigate } from 'react-router-dom';
import useMessaging from '../../hooks/useMessaging';
import { SessionCardPlaceholder } from './placeholders';
import EmptyScreen from 'components/EmptyScreen';
import PencilaltIcon from 'icons/Pencilalt.icon';
import SearchIcon from 'icons/Search.icon';
import SessionCard from './components/SessionCard';
import InfiniteScroll from 'components/InfiniteScroll';
import useChatListener from '../../pages/Chat/hooks/useChatListeners';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useInfinitePagination from 'hooks/useInfinitePagination';
import useEffectOnceWhen from 'hooks/useEffectOnce';
import useMatchMedia from 'hooks/useMatchMedia';
import { EventName } from 'modules/bus';
import uniqBy from 'lodash/uniqBy';
import api from 'api';
import { sentryError } from 'utils';

import './MessagingContacts.styles.scss';

const NUMBER_OF_PLACEHOLDERS = {
  INITIAL_LOADING_COUNT: 6,
  PAGINATED_LOADING_COUNT: 2,
} as const;

type MessagingContactsProps = {
  className?: string;
  onNewMessage: () => void;
};

const MessagingContacts: React.FC<MessagingContactsProps> = (props) => {
  const { className, onNewMessage } = props;

  const classes = classNames('messaging-contacts', className);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPhablet = useMatchMedia('isPhablet');

  const {
    currentSession,
    listingId,
    dealRoomId,
    listingEnquiryId,
    sessionId,
    setContactsOpen,
  } = useContext(MessagingContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { updateUnreadMessages, updateUnreadMessagesUnread, currentUser } =
    useContext(CurrentUserContext);

  const {
    loading,
    currentPage,
    searchString,
    items: sessions,
    setSearchString,
    setItems: setSessions,
    onContainerScrolled: onSessionScroll,
  } = useInfinitePagination<SessionType>({
    makeRequest: async (currentPage: number, searchString: string) => {
      return api.messages
        .getSessions({
          $page: currentPage.toString(),
          $limit: '10',
          $sort: '-latestMessageAt',
          $searchString: searchString,
          openSessionId: sessionId,
          listingId,
        })
        .then((res) => res.data);
    },
  });

  const { getNavigationLink } = useMessaging({
    listingId,
    dealRoomId,
    listingEnquiryId,
  });

  const finalSessions = useMemo(
    () => sessions && uniqBy(sessions, 'id'),
    [sessions],
  );

  const numOfPlaceholders = useMemo(
    () =>
      currentPage > 1
        ? NUMBER_OF_PLACEHOLDERS.PAGINATED_LOADING_COUNT
        : NUMBER_OF_PLACEHOLDERS.INITIAL_LOADING_COUNT,
    [currentPage],
  );

  const emptyPlaceholder = useMemo(
    () => (
      <div className="messaging-contacts__placeholder">
        {loading &&
          !searchString &&
          Array(numOfPlaceholders)
            .fill(1)
            .map((_, i) => <SessionCardPlaceholder key={i} />)}
      </div>
    ),
    [loading, numOfPlaceholders, searchString],
  );

  /**
   * Mark session and all messages as unread
   * @param sid session id for which you want to mark session as unread
   */
  const markSessionAsUnread = useCallback(
    async (sid: string) => {
      try {
        await api.messages.unreadAllMessages(sid);

        updateUnreadMessagesUnread();

        setSessions((prevSessions) =>
          prevSessions.map((session) =>
            sid !== session.id
              ? session
              : { ...session, hasUnreadMessages: true },
          ),
        );
      } catch (e) {
        popServerError(e);
      }
    },
    [popServerError, setSessions, updateUnreadMessagesUnread],
  );

  /**
   * Mark session and all messages as read
   * @param sid session id for which you want to mark session as read
   */
  const markSessionAsRead = useCallback(
    async (sid: string) => {
      try {
        await api.messages.readAllMessages(sid);

        if (currentUser?.totalUnreadMessages > 0) updateUnreadMessages();

        setSessions((oldSessions) =>
          oldSessions.map((session) =>
            session.id === sid
              ? {
                  ...session,
                  hasUnreadMessages: false,
                }
              : session,
          ),
        );
      } catch (e) {
        popServerError(e);
      }
    },
    [
      currentUser.totalUnreadMessages,
      popServerError,
      setSessions,
      updateUnreadMessages,
    ],
  );

  /**
   * When bulk sessions are created then make uniq sessions list
   * @param data payload from bus callback
   */
  const bulkSessionsHandler = useCallback(
    (data) => {
      const { payload: sessions } = data;

      navigate(getNavigationLink(sessions?.[0]?.id), {
        replace: true,
      });

      setSessions((oldSessions) => uniqBy([...sessions, ...oldSessions], 'id'));
    },
    [navigate, getNavigationLink, setSessions],
  );

  /**
   * If active session is one where new message arrived
   * then do not update session status
   * @param message data payload from bus callback
   */
  const messageArriverHandler = useCallback(
    (message: MessageType) => {
      const { sessionId: msgSessionId, session } = message;

      // if active session is one where message arrived then read all messages
      if (sessionId === msgSessionId && !session.isExternalCommunication)
        markSessionAsRead(msgSessionId);

      setSessions((oldSessions) => [
        { ...session, hasUnreadMessages: sessionId !== msgSessionId },
        ...oldSessions.filter((session) => session.id !== msgSessionId),
      ]);
    },
    [sessionId, markSessionAsRead, setSessions],
  );

  /**
   * When message is sent update the sessions last message
   * @param data payload from bus callback
   */
  const messageSentHandler = useCallback(
    async (data: { event: EventName; payload: MessageType }) => {
      const { sessionId } = data.payload;

      setSessions((oldSessions) => {
        const session = oldSessions.find((s) => s.id === sessionId);
        const isSessionExists = !!session;

        // read all messages from session only if there are unread messages
        if (session.hasUnreadMessages) {
          markSessionAsRead(sessionId);
        }

        if (!isSessionExists)
          return [
            {
              ...currentSession,
              latestMessageAt: new Date(),
            },
            ...oldSessions,
          ];

        return oldSessions
          .map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  latestMessageAt: new Date(),
                }
              : session,
          )
          .sort((first, second) => {
            const firstDate = new Date(first.latestMessageAt).getTime();
            const secondDate = new Date(second.latestMessageAt).getTime();

            return firstDate > secondDate ? -1 : 1;
          });
      });
    },
    [currentSession, markSessionAsRead, setSessions],
  );

  useChatListener({
    onBulkSessions: bulkSessionsHandler,
    onMessageSent: messageSentHandler,
    onMessageArrived: messageArriverHandler,
  });

  useEffectOnceWhen(() => {
    if (!sessionId) {
      navigate(getNavigationLink(sessions[0].id), { replace: true });
    }
  }, sessions?.length > 0);

  useEffect(() => {
    if (!sessionId || sessions?.some(({ id }) => id === sessionId)) return;

    (async () => {
      try {
        const { data } = await api.messages.getSessionById(sessionId);
        setSessions((prev) => [data, ...prev]);
      } catch (e) {
        sentryError(e);
        popServerError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, popServerError, setSessions]);

  useEffect(() => {
    const { id, isExternalCommunication, hasUnreadMessages } =
      currentSession || {};
    if (!isExternalCommunication && hasUnreadMessages && sessionId === id) {
      markSessionAsRead(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, sessionId]);

  return (
    <div className={classes}>
      <div className="messaging-contacts__header">
        <div className="messaging-contacts__actions">
          <p className="messaging-contacts__actions__title">{t('messaging')}</p>
          <Button
            className="contact"
            variant="outline"
            icon={PencilaltIcon}
            onClick={onNewMessage}
          >
            {t('composeMessage')}
          </Button>
        </div>

        <Input
          className="messaging-contacts__search"
          value={searchString}
          prefixNode={<SearchIcon />}
          placeholder={t('searchName')}
          onChange={(e) => setSearchString(e.target.value)}
        />
      </div>

      <InfiniteScroll
        localScroll
        className="messaging-contacts__sessions"
        onScroll={onSessionScroll}
      >
        {loading && !!searchString && <Loader />}

        {!loading && !sessions?.length ? (
          <EmptyScreen
            className="messaging-contacts__empty-screen"
            content={t('participantsForListingAreGoingToBecamaAvailable')}
            title={t('youHaveNoContactsRightNow')}
          />
        ) : (
          finalSessions?.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              markSessionAsUnread={markSessionAsUnread}
              onClick={() => {
                if (isPhablet) setContactsOpen(false);
              }}
            />
          ))
        )}

        {emptyPlaceholder}
      </InfiniteScroll>
    </div>
  );
};

export default MessagingContacts;
